/**
 * Check-In/Check-Out API Client
 *
 * Public API client for check-in wizard (no auth required - token-validated)
 * Admin API client for check-in management (auth required)
 */

import { supabase, currentSupabaseEnv } from './supabase';
import type {
  CheckinUnitInfo,
  CheckinSubmissionPayload,
  CheckinSubmissionResponse,
  CheckinImageUploadResponse,
  CheckinImageType,
  CheckinImage,
  CheckinRecord,
  CheckinWithImages,
  CheckinListParams,
  CheckinListResponse,
  CheckinCurrentStatus,
  CheckinUpdatePayload,
} from '@/types/checkin';

const API_BASE_URL = '/api/v1';

// =============================================================================
// PUBLIC API (No Auth - Token Validated)
// =============================================================================

/**
 * Get headers for public API calls
 * Includes X-Supabase-Environment to ensure correct database is used
 */
function getPublicHeaders(): HeadersInit {
  return {
    'X-Supabase-Environment': currentSupabaseEnv,
  };
}

/**
 * API Error with debug details for check-in operations
 */
export class CheckinApiError extends Error {
  status: number;
  statusText: string;
  url: string;
  timestamp: string;
  details?: string;

  constructor(message: string, response: Response, details?: string) {
    super(message);
    this.name = 'CheckinApiError';
    this.status = response.status;
    this.statusText = response.statusText;
    this.url = response.url;
    this.timestamp = new Date().toISOString();
    this.details = details;
  }

  getDebugInfo(): string {
    return [
      `Error: ${this.message}`,
      `Status: ${this.status} ${this.statusText}`,
      `URL: ${this.url}`,
      `Time: ${this.timestamp}`,
      this.details ? `\nResponse:\n${this.details}` : null,
    ].filter(Boolean).join('\n');
  }
}

/**
 * Get unit info from QR code token
 * Public endpoint - no auth required
 */
export async function getUnitByToken(token: string): Promise<CheckinUnitInfo> {
  const url = `${API_BASE_URL}/checkinout/unit/${token}`;
  const response = await fetch(url, {
    headers: getPublicHeaders(),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    let errorMessage = 'Unit not found';
    let details = errorBody;

    try {
      const errorJson = JSON.parse(errorBody);
      errorMessage = errorJson.message || errorJson.error || errorMessage;
      details = JSON.stringify(errorJson, null, 2);
    } catch {
      // Keep raw text as details
    }

    throw new CheckinApiError(errorMessage, response, details);
  }

  const data = await response.json();
  return data.unit;
}

/**
 * Submit a check-in or check-out record
 * Public endpoint - validated by unit token
 */
export async function submitCheckin(
  payload: CheckinSubmissionPayload
): Promise<CheckinSubmissionResponse> {
  const response = await fetch(`${API_BASE_URL}/checkinout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Supabase-Environment': currentSupabaseEnv,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || 'Failed to submit check-in/out');
  }

  return response.json();
}

/**
 * Upload images for a check-in record
 * Public endpoint - rate limited
 */
export async function uploadCheckinImages(
  checkinId: string,
  images: Array<{ file: File; type: CheckinImageType }>
): Promise<CheckinImageUploadResponse> {
  const formData = new FormData();

  images.forEach(({ file, type }) => {
    formData.append('images', file);
    formData.append('types', type);
  });

  const response = await fetch(`${API_BASE_URL}/checkinout/${checkinId}/images`, {
    method: 'POST',
    headers: {
      'X-Supabase-Environment': currentSupabaseEnv,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || 'Failed to upload images');
  }

  return response.json();
}

// =============================================================================
// ADMIN API (Auth Required)
// =============================================================================

/**
 * Get authorization header with Supabase token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('User not authenticated. Please log in.');
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
    'X-Supabase-Environment': currentSupabaseEnv,
  };
}

/**
 * T-018: Submit a check-in via the admin endpoint with JWT auth + Idempotency-Key.
 * Photos are sent in the same multipart request (T-011's handler accepts up to 9
 * additional images via multer.array('images')).
 *
 * The `idempotencyKey` should be a stable UUID generated when the wizard form
 * opens; reuse it across retries so the server returns the cached response
 * instead of creating a duplicate checkin.
 */
export async function submitAdminCheckin(
  payload: CheckinSubmissionPayload,
  idempotencyKey: string,
  photos: Array<{ file: File; type: CheckinImageType }> = [],
): Promise<CheckinSubmissionResponse> {
  const headers = await getAuthHeaders();
  const formData = new FormData();
  formData.append('unit_id', payload.unit_id);
  formData.append('event_type', payload.event_type);
  formData.append('driver_first_name', payload.driver_first_name);
  formData.append('driver_last_name', payload.driver_last_name);
  if (payload.driver_phone) formData.append('driver_phone', payload.driver_phone);
  if (payload.odometer_reading) formData.append('odometer_reading', String(payload.odometer_reading));
  if (payload.fuel_level) formData.append('fuel_level', String(payload.fuel_level));
  if (payload.def_level) formData.append('def_level', String(payload.def_level));
  if (payload.damage_report) formData.append('damage_report', payload.damage_report);
  // The person checking in chose what the noted damage does; omitted = the Settings card decides.
  if (typeof payload.hold_for_repair === 'boolean') {
    formData.append('hold_for_repair', String(payload.hold_for_repair));
  }
  formData.append('condition_comments', payload.condition_comments);
  formData.append('signature_data', payload.signature_data);
  formData.append('signature_hash', payload.signature_hash);
  formData.append('acknowledged', String(payload.acknowledged));
  for (const { file } of photos) {
    formData.append('images', file);
  }

  const response = await fetch(`${API_BASE_URL}/admin/checkins`, {
    method: 'POST',
    headers: {
      Authorization: (headers as Record<string, string>).Authorization,
      'X-Supabase-Environment': currentSupabaseEnv,
      'Idempotency-Key': idempotencyKey,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    let errorMessage = 'Failed to submit check-in';
    let details = errorBody;
    try {
      const parsed = JSON.parse(errorBody);
      errorMessage = parsed.message || parsed.error || errorMessage;
      details = JSON.stringify(parsed, null, 2);
    } catch {
      // keep raw text
    }
    throw new CheckinApiError(errorMessage, response, details);
  }

  return response.json();
}

/**
 * Generic admin fetch wrapper
 */
async function adminFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers = await getAuthHeaders();
  const url = `${API_BASE_URL}/admin${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `API Error: ${response.status}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

/**
 * List check-in records with filtering and pagination
 * Admin endpoint - RBAC filtered
 */
export async function listCheckins(params: CheckinListParams = {}): Promise<CheckinListResponse> {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.set('page', String(params.page));
  if (params.limit) queryParams.set('limit', String(params.limit));

  // Support both single and multiple company/group filters
  if (params.company_id) {
    queryParams.set('company_id', params.company_id);
  } else if (params.company_ids && params.company_ids.length > 0) {
    params.company_ids.forEach(id => queryParams.append('company_ids', id));
  }

  if (params.group_id) {
    queryParams.set('group_id', params.group_id);
  } else if (params.group_ids && params.group_ids.length > 0) {
    params.group_ids.forEach(id => queryParams.append('group_ids', id));
  }

  if (params.unit_id) queryParams.set('unit_id', params.unit_id);
  if (params.event_type) queryParams.set('event_type', params.event_type);
  if (params.driver_name) queryParams.set('driver_name', params.driver_name);
  if (params.start_date) queryParams.set('start_date', params.start_date);
  if (params.end_date) queryParams.set('end_date', params.end_date);
  if (params.sort_by) queryParams.set('sort_by', params.sort_by);
  if (params.sort_order) queryParams.set('sort_order', params.sort_order);

  // Enrichment flags
  if (params.include_images) queryParams.set('include_images', 'true');
  if (params.include_groups) queryParams.set('include_groups', 'true');

  // Additional filters
  if (params.review_status) queryParams.set('review_status', params.review_status);
  if (params.has_damage) queryParams.set('has_damage', 'true');

  const query = queryParams.toString();
  return adminFetch<CheckinListResponse>(`/checkins${query ? `?${query}` : ''}`);
}

/**
 * Get a single check-in record by ID with images
 * Admin endpoint - RBAC filtered
 */
export async function getCheckinById(id: string): Promise<CheckinWithImages> {
  const response = await adminFetch<{ checkin: CheckinRecord & { unit_checkin_images?: CheckinImage[] } }>(`/checkins/${id}`);
  const checkin = response.checkin;

  // Transform backend field name to frontend type
  // Backend returns unit_checkin_images, frontend expects images
  const images = checkin.unit_checkin_images || [];
  delete checkin.unit_checkin_images;

  return {
    ...checkin,
    images,
  };
}

/**
 * Get check-in history for a specific unit
 * Admin endpoint - RBAC filtered
 */
export async function getUnitCheckinHistory(
  unitId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<CheckinRecord[]> {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.offset) queryParams.set('offset', String(params.offset));

  const query = queryParams.toString();
  const response = await adminFetch<{ checkins: CheckinRecord[] }>(
    `/checkins/unit/${unitId}${query ? `?${query}` : ''}`
  );
  return response.checkins;
}

/**
 * Get current check-in status for a unit
 * Admin endpoint - RBAC filtered
 */
export async function getUnitCurrentStatus(unitId: string): Promise<CheckinCurrentStatus | null> {
  const response = await adminFetch<{ status: CheckinCurrentStatus | null }>(
    `/checkins/current/${unitId}`
  );
  return response.status;
}

/**
 * Delete a check-in record (superadmin only)
 * Admin endpoint - superadmin required
 */
export async function deleteCheckin(id: string): Promise<void> {
  await adminFetch(`/checkins/${id}`, { method: 'DELETE' });
}

/**
 * Update a check-in record (admin edit)
 * Admin endpoint - editable fields only, signature immutable
 */
export async function updateCheckin(id: string, payload: CheckinUpdatePayload): Promise<{ checkin: CheckinRecord }> {
  return adminFetch<{ checkin: CheckinRecord }>(`/checkins/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

/**
 * Upload an additional photo to an existing check-in
 * Admin endpoint
 */
export async function uploadCheckinPhoto(
  checkinId: string,
  file: File,
  imageType: CheckinImageType
): Promise<{ uploaded: Array<{ id: string }> }> {
  const headers = await getAuthHeaders();
  const formData = new FormData();
  formData.append('images', file);
  formData.append('types', imageType);

  const url = `${API_BASE_URL}/admin/checkins/${checkinId}/images`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: (headers as Record<string, string>).Authorization,
      'X-Supabase-Environment': currentSupabaseEnv,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || 'Failed to upload photo');
  }

  return response.json();
}

/**
 * Remove a photo from an existing check-in
 * Admin endpoint
 */
export async function deleteCheckinPhoto(checkinId: string, imageId: string): Promise<void> {
  await adminFetch(`/checkins/${checkinId}/images/${imageId}`, { method: 'DELETE' });
}

/**
 * Create a work order from a check-in's damage report
 * Admin endpoint
 */
export async function createWorkOrderFromCheckin(
  checkinId: string
): Promise<{ work_order: { id: string; wo_number: string } }> {
  return adminFetch<{ work_order: { id: string; wo_number: string } }>(
    `/checkins/${checkinId}/work-order`,
    { method: 'POST' }
  );
}

// =============================================================================
// EXPORT
// =============================================================================

/**
 * Export filtered check-ins as Excel (.xlsx)
 * Streams a branded workbook from the server.
 */
export async function exportCheckins(
  params: CheckinListParams,
  format: 'xlsx' = 'xlsx'
): Promise<Blob> {
  const headers = await getAuthHeaders();
  const queryParams = new URLSearchParams();
  queryParams.set('format', format);

  if (params.company_ids?.length) {
    params.company_ids.forEach(id => queryParams.append('company_ids', id));
  }
  if (params.group_ids?.length) {
    params.group_ids.forEach(id => queryParams.append('group_ids', id));
  }
  if (params.company_id) queryParams.set('company_id', params.company_id);
  if (params.group_id) queryParams.set('group_id', params.group_id);
  if (params.unit_id) queryParams.set('unit_id', params.unit_id);
  if (params.driver_name) queryParams.set('driver_name', params.driver_name);
  if (params.start_date) queryParams.set('start_date', params.start_date);
  if (params.end_date) queryParams.set('end_date', params.end_date);
  if (params.event_type) queryParams.set('event_type', params.event_type);
  if (params.review_status) queryParams.set('review_status', params.review_status);
  if (params.has_damage !== undefined) {
    queryParams.set('has_damage', params.has_damage ? 'true' : 'false');
  }
  if (params.sort_by) queryParams.set('sort_by', params.sort_by);
  if (params.sort_order) queryParams.set('sort_order', params.sort_order);

  const url = `${API_BASE_URL}/admin/checkins/export?${queryParams}`;
  const response = await fetch(url, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || 'Failed to export check-ins');
  }

  return response.blob();
}

// =============================================================================
// SCHEDULED CHECK-INS
// =============================================================================

export interface ScheduledCheckin {
  id: string;
  unit_id: string;
  company_id: string;
  group_id?: string;
  event_type: 'check_in' | 'check_out';
  scheduled_date: string;
  scheduled_time?: string;
  driver_first_name?: string;
  driver_last_name?: string;
  driver_phone?: string;
  driver_email?: string;
  expected_driver_name?: string;
  expected_driver_phone?: string;
  expected_driver_email?: string;
  notes?: string;
  source_type: 'manual' | 'reservation' | 'recurring';
  source_quote_id?: string;
  source_quote_number?: string;
  production_title?: string;
  unit_type?: string;
  status: 'pending' | 'created' | 'cancelled' | 'skipped';
  created_checkin_id?: string;
  created_at: string;
  // Joined relations
  unit?: { id: string; unit_code: string; name: string; model_code: string };
  company?: { id: string; name: string };
  group?: { id: string; name: string };
}

export interface ScheduledCheckinListResponse {
  scheduled_checkins: ScheduledCheckin[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/**
 * List scheduled check-ins with filters
 */
export async function listScheduledCheckins(params: {
  status?: string;
  unit_id?: string;
  company_id?: string;
  event_type?: string;
  from_date?: string;
  to_date?: string;
  source_type?: string;
  page?: number;
  limit?: number;
} = {}): Promise<ScheduledCheckinListResponse> {
  return adminFetch<ScheduledCheckinListResponse>(
    `/scheduled-checkins?${new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => [k, String(v)])
    )}`,
    { method: 'GET' }
  );
}

/**
 * Get today's pending scheduled check-ins
 */
export async function getTodaysSchedule(): Promise<{
  scheduled_checkins: ScheduledCheckin[];
  date: string;
}> {
  return adminFetch('/scheduled-checkins/today', { method: 'GET' });
}

/**
 * Get a single scheduled check-in by ID
 */
export async function getScheduledCheckin(id: string): Promise<ScheduledCheckin> {
  return adminFetch(`/scheduled-checkins/${id}`, { method: 'GET' });
}

/**
 * Create a manual scheduled check-in
 */
export async function createScheduledCheckin(data: {
  unit_id: string;
  company_id: string;
  group_id?: string;
  event_type: 'check_in' | 'check_out';
  scheduled_date: string;
  scheduled_time?: string;
  driver_first_name?: string;
  driver_last_name?: string;
  driver_phone?: string;
  driver_email?: string;
  notes?: string;
}): Promise<ScheduledCheckin> {
  return adminFetch('/scheduled-checkins', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a scheduled check-in
 */
export async function updateScheduledCheckin(
  id: string,
  data: Partial<{
    scheduled_date: string;
    scheduled_time: string;
    event_type: string;
    driver_first_name: string;
    driver_last_name: string;
    driver_phone: string;
    driver_email: string;
    notes: string;
    status: string;
  }>
): Promise<ScheduledCheckin> {
  return adminFetch(`/scheduled-checkins/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Fulfill a scheduled check-in (link to actual check-in record)
 */
export async function fulfillScheduledCheckin(
  id: string,
  checkinId: string
): Promise<ScheduledCheckin> {
  return adminFetch(`/scheduled-checkins/${id}/fulfill`, {
    method: 'POST',
    body: JSON.stringify({ checkin_id: checkinId }),
  });
}

/**
 * Cancel a scheduled check-in (soft delete)
 */
export async function cancelScheduledCheckin(id: string): Promise<{ status: string; id: string }> {
  return adminFetch(`/scheduled-checkins/${id}`, { method: 'DELETE' });
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Generate SHA-256 hash of signature data for tamper detection
 */
export async function generateSignatureHash(signatureData: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(signatureData);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Compress image file before upload
 * Uses canvas to resize and compress
 */
export async function compressImage(
  file: File,
  maxWidth: number = 2048,
  maxHeight: number = 2048,
  quality: number = 0.8
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Calculate new dimensions
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Create canvas and draw image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          // Create new file with original name
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          resolve(compressedFile);
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Validate that all required photos are present
 */
export function validateRequiredPhotos(
  photos: Partial<Record<CheckinImageType, File | null>>
): { valid: boolean; missing: CheckinImageType[] } {
  const requiredTypes: CheckinImageType[] = ['front', 'back', 'driver_side', 'passenger_side'];
  const missing: CheckinImageType[] = [];

  for (const type of requiredTypes) {
    if (!photos[type]) {
      missing.push(type);
    }
  }

  return {
    valid: missing.length === 0,
    missing,
  };
}

/**
 * Format operator name for display
 */
export function formatOperatorName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim();
}

/** @deprecated Use formatOperatorName instead */
export const formatDriverName = formatOperatorName;

/**
 * Format event type for display
 */
export function formatEventType(eventType: 'check_in' | 'check_out'): string {
  return eventType === 'check_in' ? 'Check In' : 'Check Out';
}
